import React, { useEffect, useState } from 'react';
import { Input } from '../../BasicComponents/Input/Input';
import { Button } from '../../BasicComponents/Button/Button';
import { currencySymbol } from '../../../utils/currency';
import type { DiscountInputProps } from './types';

// ─── DiscountInput ────────────────────────────────────────────────────────────

export function DiscountInput({
  value,
  discountType,
  onValueChange,
  onTypeChange,
  onApply,
  maxValue,
  disabled = false,
  currency = 'INR',
  className,
}: DiscountInputProps) {
  const symbol = currencySymbol(currency);
  const [inputStr, setInputStr] = useState(value > 0 ? String(value) : '');

  // A programmatic `value` change (modal re-open, voucher prefill, type
  // toggle) must update the visible text. Skip while the text already
  // parses to `value` so in-progress typing like "12." is not clobbered.
  useEffect(() => {
    const current = parseFloat(inputStr);
    const same = value === 0 ? inputStr === '' || current === 0 : current === value;
    if (!same) setInputStr(value > 0 ? String(value) : '');
  }, [value, discountType]);

  const parsedValue = parseFloat(inputStr);
  const isOverMax =
    maxValue !== undefined &&
    discountType === 'flat' &&
    !isNaN(parsedValue) &&
    parsedValue > maxValue;

  function handleInputChange(val: string) {
    const raw = val.replace(/[^0-9.]/g, '');
    setInputStr(raw);
    const num = parseFloat(raw);
    if (!isNaN(num)) onValueChange(num);
    else if (raw === '' || raw === '.') onValueChange(0);
  }

  function handleApply() {
    if (!isOverMax && !isNaN(parsedValue)) onApply();
  }

  return (
    <div className={`flex flex-wrap items-start gap-1 w-full min-w-0 ${className ?? ''}`}>
      {/* Type toggle — flat amount / % */}
      <div className="flex rounded border border-neutral-200 dark:border-neutral-600 overflow-hidden shrink-0 self-stretch">
        <button
          type="button"
          onClick={() => !disabled && onTypeChange('flat')}
          disabled={disabled}
          aria-pressed={discountType === 'flat'}
          className={[
            'px-2.5 text-sm font-semibold transition-colors select-none',
            discountType === 'flat'
              ? 'bg-brand-500 text-white'
              : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {symbol}
        </button>
        <button
          type="button"
          onClick={() => !disabled && onTypeChange('percent')}
          disabled={disabled}
          aria-pressed={discountType === 'percent'}
          className={[
            'px-2.5 text-sm font-semibold transition-colors select-none',
            discountType === 'percent'
              ? 'bg-brand-500 text-white'
              : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          %
        </button>
      </div>

      {/* Value input */}
      <div className="flex-1 min-w-0 basis-24">
        <Input
          value={inputStr}
          onChange={handleInputChange}
          inputMode="decimal"
          placeholder={discountType === 'percent' ? '0–100' : '0.00'}
          disabled={disabled}
          error={isOverMax ? `Max ${symbol}${maxValue}` : undefined}
          aria-label="Discount value"
        />
      </div>

      {/* Apply button */}
      <Button
        variant="secondary"
        size="md"
        onClick={handleApply}
        disabled={disabled || isOverMax || isNaN(parsedValue)}
        aria-label="Apply discount"
        className="shrink-0"
      >
        Apply
      </Button>
    </div>
  );
}
